/**
 * StageTimeline — user-facing chart of relationship stage trajectory.
 * Memory Plan v1, Phase 4.
 *
 * Renders both the user's perceived stage and the inferred match stage as
 * dual line charts with progression / regression markers and a current-stage
 * read-out. Asynchronicity (≥2 stage gap) gets an explicit banner.
 */
import React, { useEffect, useState, useCallback } from "react";
import {
  Loader2,
  AlertCircle,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Minus,
  RefreshCw,
} from "lucide-react";
import { authAxios } from "../../utils/auth";
import StageSyncBar from "./StageSyncBar";
import StageInfoDialog from "./StageInfoDialog";

const CONNECTION_STAGES = [
  "NONE",
  "SPARK",
  "OPENING",
  "RESONANCE",
  "SAFETY",
  "CURIOSITY",
  "ATTRACTION",
  "VULNERABILITY",
  "ALIGNMENT",
  "MOMENTUM",
  "INTIMACY",
  "INTEGRATION",
  "COMMITMENT",
  "UNION",
];

const CHANGE_ICON = {
  progression: <TrendingUp className="w-3 h-3 text-emerald-400" />,
  regression: <TrendingDown className="w-3 h-3 text-rose-400" />,
  confirmation: <Minus className="w-3 h-3 text-white/40" />,
  initial: <Minus className="w-3 h-3 text-divine-gold/70" />,
};

function stageLabel(stage) {
  if (!stage) return "—";
  const parts = stage.split("_");
  const idx = parseInt(parts[1], 10);
  return `${idx} · ${CONNECTION_STAGES[idx] || parts.slice(2).join(" ")}`;
}

export default function StageTimeline({ matchId, refreshKey = 0 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastLoadedAt, setLastLoadedAt] = useState(null);

  const load = useCallback(async () => {
    if (!matchId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await authAxios.get(
        `/relationship/${matchId}/stages`,
        {
          params: { _: Date.now() },
          headers: { "Cache-Control": "no-store" },
        }
      );
      setData(res.data);
      setLastLoadedAt(new Date());
    } catch (e) {
      const status = e?.response?.status;
      setError(`Stages load failed${status ? ` (${status})` : ""}`);
    } finally {
      setLoading(false);
    }
  }, [matchId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  if (!matchId) {
    return (
      <div className="text-white/40 text-sm p-4" data-testid="stage-empty-no-match">
        Select a match to see stage trajectory.
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex items-center justify-center p-6 text-white/60"
        data-testid="stage-loading"
      >
        <Loader2 className="w-4 h-4 animate-spin mr-2" />
        Loading stage trajectory…
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="flex items-start gap-2 p-4 text-rose-300 text-sm"
        data-testid="stage-error"
      >
        <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
        <span>{error}</span>
      </div>
    );
  }

  const stageHistory = data?.stage_history || [];
  const asynchronicity = data?.asynchronicity;
  const userName = data?.user_persona_name || "You";
  const matchName = data?.match_name || "this person";

  // Templated calibration prompt for the asynchronicity click-to-explore.
  // Direction-aware in that it names both parties; user can edit before send.
  const buildAsyncExploreText = () => {
    if (!asynchronicity) return "";
    const youFmt = stageLabel(asynchronicity.user_stage);
    const themFmt = stageLabel(asynchronicity.match_stage);
    return (
      `I want to look at something. You've been reading me at ${youFmt} ` +
      `while reading ${matchName} at ${themFmt}. That's a ${asynchronicity.gap}-stage ` +
      `gap and different emotional places — can we explore that?`
    );
  };

  const handleAsyncExplore = () => {
    const text = buildAsyncExploreText();
    if (!text) return;
    window.dispatchEvent(
      new CustomEvent("eros:inject-prompt", { detail: { text } })
    );
  };

  return (
    <div className="space-y-4" data-testid="stage-timeline">
      <header className="space-y-1">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-cinzel text-divine-gold text-lg">Stage trajectory</h3>
          <div className="flex items-center gap-1 shrink-0">
            <StageInfoDialog />
            <button
              type="button"
              onClick={load}
              disabled={loading}
              className="text-white/50 hover:text-divine-gold transition-colors p-1 -mr-1"
              title="Refresh stage trajectory"
              data-testid="stage-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>
        {lastLoadedAt ? (
          <p className="text-white/30 text-[10px]">
            Last refreshed {lastLoadedAt.toLocaleTimeString()}
          </p>
        ) : null}
      </header>

      <StageSyncBar matchId={matchId} refreshKey={refreshKey} />

      {/* Clickable asynchronicity banner (Feb 21, 2026): the whole row is a
          button that pre-fills the chat with a calibration prompt about the
          stage gap. Red AlertTriangle icon + red CTA pop; rest of the banner
          stays amber so the alert hierarchy doesn't shout (Style C from the
          UX confirmation). */}
      {asynchronicity ? (
        <button
          type="button"
          onClick={handleAsyncExplore}
          data-testid="stage-asynchronicity-banner"
          className="w-full text-left flex items-start gap-2 border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/15 rounded-md px-3 py-2 text-amber-200 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-amber-400/40"
        >
          <AlertTriangle
            className="w-4 h-4 mt-0.5 shrink-0 text-rose-400"
            data-testid="stage-asynchronicity-icon"
          />
          <span className="leading-snug">
            <span className="text-rose-300 font-medium">Asynchronicity:</span>{" "}
            you are at{" "}
            <strong>{stageLabel(asynchronicity.user_stage)}</strong> while{" "}
            {matchName} is at{" "}
            <strong>{stageLabel(asynchronicity.match_stage)}</strong> (gap of{" "}
            {asynchronicity.gap}).{" "}
            <span className="text-rose-300 font-medium underline underline-offset-2 decoration-rose-400/50">
              Click here to explore this.
            </span>
          </span>
        </button>
      ) : null}

      <section className="space-y-2" data-testid="stage-history-section">
        <h4 className="text-divine-gold/80 text-xs uppercase tracking-wide">
          History ({stageHistory.length})
        </h4>
        {stageHistory.length === 0 ? (
          <div
            className="text-white/40 text-sm border border-white/10 rounded-md p-3"
            data-testid="stage-empty-no-history"
          >
            Stage trajectory not yet inferred. As you have coaching sessions, Eros will
            chart the journey.
          </div>
        ) : (
          <ol className="space-y-1.5">
            {stageHistory
              .slice()
              .reverse()
              .map((s) => (
                <li
                  key={s.entry_id}
                  data-testid={`stage-entry-${s.entry_id}`}
                  className="flex items-start gap-2 text-xs px-2 py-1.5 border border-white/5 rounded bg-divine-black/30"
                >
                  <span className="mt-0.5">
                    {CHANGE_ICON[s.change_type] || CHANGE_ICON.confirmation}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span className="text-white/85">
                        <span className="text-divine-gold/70">
                          {s.stage_for === "user" ? userName : matchName}
                        </span>{" "}
                        → {stageLabel(s.stage)}
                      </span>
                      <span className="text-white/30">
                        {s.recorded_at?.split("T")[0]}
                      </span>
                    </div>
                    {s.evidence_summary ? (
                      <div className="text-white/40 italic mt-0.5 break-words">
                        {s.evidence_summary}
                      </div>
                    ) : null}
                  </div>
                </li>
              ))}
          </ol>
        )}
      </section>
    </div>
  );
}
